import { ActivityItem, ChoiceGroup, DefaultButton, DirectionalHint, Dropdown, FontIcon, IActivityItemProps, IChoiceGroupOption, IDropdownOption, IPanelStyles, IPersonaProps, IPersonaSharedProps, IStackTokens, Label, mergeStyles, MessageBar, MessageBarButton, MessageBarType, Panel, PanelType, PrimaryButton, ResponsiveMode, ScrollablePane, ScrollbarVisibility, Spinner, SpinnerSize, Stack, StackItem, TextField, Toggle, TooltipHost } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { getEmailTemplateHtml } from "../../src/taskpane/components/helpers/shareFileEmailTemplateHelper";
import { Fileview } from "../../src/taskpane/components/_v2/components/Explorer/Fileview/Fileview";
import { IGraphCreateShareableLinkRequest, ShareableLinkRequestScope, ShareableLinkRequestType } from "../../src/taskpane/components/_v2/redux/api/graph/api-graph-interfaces";
import { useLazySendMailQuery } from "../../src/taskpane/components/_v2/redux/api/graph/api-graph-slice";
import { useLazyGetSharingInformationQuery } from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { PeopleFinder } from "../../src/taskpane/components/_v2/_shared/PeopleFinder/PeopleFinder";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { createItemShareableLink, getFileItemIdFromRelativePath, getItemWebViewUrl } from "../../src/taskpane/services/GraphService";
import { isMobile, mobileListItemStyle } from "../../src/taskpane/styles/mobileStyles";
import authModule from "../../utilities/AuthModule";
import generalModule from "../../utilities/GeneralModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { useMailSendPopUpAuth } from "../Hooks/useMailSendPopUpAuth";
import { IPageSettings } from "../../src/taskpane/interfaces/IPageSettings";
import { FileIconType, getFileTypeIconAsUrl, IFileTypeIconOptions } from "@fluentui/react-file-type-icons";
import { contentManager, SharePointFolderContentTypeIds } from "../../src/taskpane/config/constants";
import { IAuthAccount } from "../../src/taskpane/interfaces/IAuthTokenResultDto";
import { tryEncodeUriComponent } from "../../utilities/EncodeDecode";

const scrollStyle = mergeStyles({
    position: "relative",
    height: "calc(100vh - 160px)",
});

const truncatedFilenameStyle = mergeStyles({
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

const stackTokens: IStackTokens = { childrenGap: 20 };

const panelStyles: Partial<IPanelStyles> = {
    content: {
        paddingLeft: 15,
        paddingRight: 15
    }
}

export const enum SharePanelMode {
    Share,
    Copy
}

type CopyLinkItemResult = { item: any; link?: string; error?: string };

type CopyLinksBatch = {
    itemResults: CopyLinkItemResult[];
    hasFailedLinks: boolean;
    errorMessage: string;
};

type ShareMailResult = { ok: boolean; error?: string; filename?: string };

type ManualCopyItem = { item: any; link: string };

type CopyStatus = {
    manualItems: ManualCopyItem[] | null;
    manualCopyCompleted: boolean;
    manualCopyButtonFailed: boolean;
    manualCopyHasFailedLinks: boolean;
    copiedLinkCount: number;
};

const minItems = 5;
const maxConcurrentItemOperations = 5;
const initialCopyStatus: CopyStatus = {
    manualItems: null,
    manualCopyCompleted: false,
    manualCopyButtonFailed: false,
    manualCopyHasFailedLinks: false,
    copiedLinkCount: 0,
};

const normalizeRecipients = (recipients?: string[]): string[] => {
    return (recipients ?? [])
        .map(recipient => recipient?.trim())
        .filter((recipient): recipient is string => !!recipient);
};

async function mapItemsWithLimit<TResult>(
    items: any[],
    mapper: (item: any) => Promise<TResult>
): Promise<TResult[]> {
    const results: TResult[] = new Array(items.length);
    let nextIndex = 0;

    const worker = async () => {
        while (nextIndex < items.length) {
            const currentIndex = nextIndex;
            nextIndex += 1;
            results[currentIndex] = await mapper(items[currentIndex]);
        }
    };

    const workerCount = Math.min(maxConcurrentItemOperations, items.length);
    const workers: Promise<void>[] = [];
    for (let i = 0; i < workerCount; i++) {
        workers.push(worker());
    }

    await Promise.all(workers);
    return results;
}

const navigatorClipboardWriteText = (text: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const textArea = document.createElement("textarea");
        try {
            textArea.value = text;
            textArea.setAttribute("readonly", "");
            textArea.style.position = "fixed";
            textArea.style.opacity = "0";
            document.body.appendChild(textArea);
            textArea.select();
            if (document.execCommand("copy")) {
                resolve();
            }
            else {
                reject(new Error("Clipboard copy was not accepted by the browser."));
            }
        } catch (e) {
            reject(e);
        } finally {
            if (textArea.parentNode) {
                textArea.parentNode.removeChild(textArea);
            }
        }
    });
}

export interface ICMFileSharePanelProps {
    panelIsOpen: boolean;
    closePanel: () => void;
    items: any[];
    mode: SharePanelMode;
    isContainer?: boolean;
    hideSharingInfo?: boolean;
    userAccount?: IAuthAccount;
}

export const CMFileSharePanel = ({ panelIsOpen, closePanel, items, mode, isContainer, hideSharingInfo, userAccount }: ICMFileSharePanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);
    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const user = useSelector((state: RootState) => state.user);

    const canCreateSharingLink = React.useMemo(() => !items?.some(x => x?.EffectiveBasePermissions?.canEditListItem === false), [items]);

    const [message, setMessage] = React.useState<string>('');
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [filenameWithoutExtention, setFilenameWithoutExtention] = React.useState<string>('');
    const [blockDownload, setBlockDownload] = React.useState(false);
    const [isCreatingLink, setIsCreatingLink] = React.useState(false);
    const [sharedSuccess, setSharedSuccess] = React.useState(false);
    const [copyStatus, setCopyStatus] = React.useState<CopyStatus>(initialCopyStatus);
    const copiedResetTimerRef = React.useRef<number | null>(null);
    const operationIdRef = React.useRef<number>(0);

    const clearCopiedResetTimer = React.useCallback(() => {
        if (copiedResetTimerRef.current) {
            window.clearTimeout(copiedResetTimerRef.current);
            copiedResetTimerRef.current = null;
        }
    }, []);

    React.useEffect(() => () => {
        operationIdRef.current += 1;
        clearCopiedResetTimer();
    }, [clearCopiedResetTimer]);

    React.useEffect(() => {
        if (!panelIsOpen) {
            operationIdRef.current += 1;
            setIsCreatingLink(false);
            setSharedSuccess(false);
            setErrorMessage("");
            setCopyStatus(initialCopyStatus);
            clearCopiedResetTimer();
        }
    }, [clearCopiedResetTimer, panelIsOpen]);

    const isCurrentOperation = React.useCallback((operationId: number): boolean => {
        return operationIdRef.current === operationId;
    }, []);

    const getActionableItems = React.useCallback((): any[] => {
        return (items ?? []).filter(item => !!item);
    }, [items]);

    const [types] = React.useState<IDropdownOption[]>([
        { key: 'edit', text: languageStrings.CanEditMakesChanges },
        { key: 'view', text: languageStrings.CanViewCannotMakeChanges }
    ]);
    const [selectedType, setSelectedType] = React.useState<IDropdownOption>(types[0]);

    const [scopes] = React.useState<IChoiceGroupOption[]>([
        // { key: ShareableLinkRequestScope.Anonymous, text: languageStrings.Anyone, disabled: true },
        { key: ShareableLinkRequestScope.Organization, text: languageStrings.MyOrganization },
        { key: ShareableLinkRequestScope.Users, text: languageStrings.PeopleYouChoose }
    ]);
    const [selectedScope, setSelectedScope] = React.useState<ShareableLinkRequestScope>(
        mode === SharePanelMode.Copy
            ? ShareableLinkRequestScope.Organization
            : ShareableLinkRequestScope.Users
    );

    const [activityItem, setActivityItem] = React.useState<IActivityItemProps>();
    const [tooltipContent, setTooltipContent] = React.useState<JSX.Element>();

    const [selectedPeople, setSelectedPeople] = React.useState<string[]>([]);
    const selectedPersonasChanged = (selectedPersonas: IPersonaProps[], customTextValue?: string) => {
        const people = selectedPersonas.map(x => x.secondaryText);
        if (customTextValue) {
            people.push(customTextValue);
        }
        setSelectedPeople(normalizeRecipients(people));
    }

    const [getSharingInformation] = useLazyGetSharingInformationQuery();
    const [sendMail] = useLazySendMailQuery();

    const { isMailSendTokenAvailable, isMailSendTokenRequestInProgress, requestMailSendToken } = useMailSendPopUpAuth();

    const isInOneDrive = React.useRef(currentDirectory?.type == ComponentTypes.OneDrive || currentDirectory?.type == ComponentTypes.OneDriveSubFolder);

    const [showMoreIsClicked, setShowMore] = React.useState<boolean>(false);
    const [sectionStyle, setSectionStyle] = React.useState<any>();
    React.useEffect(() => {
        const itemHeight = isMobile ? mobileListItemStyle.height : 40;
        const itemCount = items?.length ?? 0;
        let containerHeight = itemHeight * itemCount;

        if (!showMoreIsClicked) {
            containerHeight = itemCount > minItems ? itemHeight * minItems : itemHeight * itemCount;
        }

        setSectionStyle(
            mergeStyles({
                maxHeight: containerHeight,
                overflow: "hidden",
                transition: "max-height 0.5s ease-in-out",
            })
        );
    }, [items, showMoreIsClicked]);

    const showMore = (items?.length ?? 0) > minItems && (
        <div
            className="xb-show-more-action"
            onClick={() => setShowMore((prevShowMoreIsClicked) => !prevShowMoreIsClicked)}
            style={{ marginTop: "5px" }}
        >
            <FontIcon iconName={!showMoreIsClicked ? "ChevronDown" : "ChevronUp"} />
            <span>{!showMoreIsClicked ? languageStrings.ShowMore : languageStrings.ShowLess}</span>
        </div>
    );

    React.useEffect(() => {
        if (!hideSharingInfo && items?.length === 1) {
            const item = items[0];
            (async () => {
                try {
                    let siteUrl: string;
                    if (item.currentLocation) {
                        const currentLocation: IPageSettings = item.currentLocation;
                        isInOneDrive.current = currentLocation.type == ComponentTypes.OneDrive || currentLocation.type == ComponentTypes.OneDriveSubFolder;
                        siteUrl = isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentLocation.webUrl;
                    }
                    else {
                        siteUrl = isInOneDrive.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl;
                    }

                    const sharingInformation = await getSharingInformation({
                        siteUrl,
                        sharePointListId: item.sharePointListId || item.sharepointIds?.listId || currentSharepointFolder.sharepointListId,
                        uniqueId: item.UniqueId ? item.UniqueId.replace("{", "").replace("}", "") : item.sharepointIds.listItemUniqueId,
                        accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                    }).unwrap();

                    const personas: IPersonaSharedProps[] = sharingInformation?.permissionsInformation?.principals?.map(principal => {
                        let initials = "";

                        const parts = principal.principal.name?.split(" ");

                        if (parts.length === 1) {
                            initials = `${parts[0].charAt(0)}`;
                        }
                        else if (parts.length > 1) {
                            initials = `${parts[0].charAt(0)}${parts[1].charAt(0)}`;
                        }

                        const persona: IPersonaSharedProps = {
                            text: principal.principal.name,
                            imageInitials: initials
                        }
                        return persona;
                    });

                    if (personas?.length > 0) {
                        let activityDescription: any;
                        if (personas.length === 1) {
                            activityDescription = [<span key={1}>{personas[0].text}</span>]
                        }
                        else if (personas.length === 2) {
                            activityDescription = [<span key={1}>{personas[0].text}, {personas[1].text}</span>]
                        }
                        else if (personas.length === 3) {
                            activityDescription = [<span key={1}>{personas[0].text}, {personas[1].text} and 1 other</span>]
                        }
                        else if (personas.length > 3) {
                            activityDescription = [<span key={1}>{personas[0].text}, {personas[1].text} and {personas.length - 2} others</span>]
                        }

                        setTooltipContent(
                            <>
                                <Label>{languageStrings.SharedWith}</Label>
                                <ul>
                                    {personas.map((persona, index) => {
                                        return (
                                            <li key={index}>{persona.text}</li>
                                        )
                                    })}
                                </ul>
                            </>
                        );

                        setActivityItem({
                            activityDescription: activityDescription,
                            activityPersonas: personas,
                            isCompact: true,
                        });
                    }
                } catch (error) {
                    logger(error, "CMFileSharePanel.tsx (SharingInformation)");
                }
            })();
        }
    }, [items]);

    React.useEffect(() => {
        if (mode === SharePanelMode.Copy) return;
        if (!isCreatingLink) return;
        if (!isMailSendTokenAvailable) return;

        const operationId = operationIdRef.current;

        (async () => {
            try {
                const actionableItems = getActionableItems();
                if (actionableItems.length === 0) {
                    if (isCurrentOperation(operationId)) {
                        setErrorMessage(languageStrings.ErrorMessageGeneric);
                        setSharedSuccess(false);
                    }
                    return;
                }

                const recipients = normalizeRecipients(selectedPeople);
                if (selectedScope === ShareableLinkRequestScope.Users && recipients.length === 0) {
                    if (isCurrentOperation(operationId)) {
                        setErrorMessage(languageStrings.PleaseSelectSomeoneShare);
                        setSharedSuccess(false);
                    }
                    return;
                }

                if (isCurrentOperation(operationId)) {
                    setErrorMessage("");
                }

                let sharedGraphTokenPromise: Promise<string> | undefined;
                const getSharedGraphToken = async (): Promise<string> => {
                    if (!sharedGraphTokenPromise) {
                        sharedGraphTokenPromise = authModule.getGraphAccessToken();
                    }
                    return sharedGraphTokenPromise;
                };

                const mailAccessToken = await authModule.getMailSendAccessToken();
                const results = await mapItemsWithLimit(actionableItems, async item => sendShareMailForItem(item, recipients, getSharedGraphToken, mailAccessToken));

                if (!isCurrentOperation(operationId)) return;

                const anyFailed = results.some(r => !r.ok);
                const firstFilename = results.find(r => r.ok)?.filename ?? '';
                const aggregateError = results.find(r => r.error)?.error ?? languageStrings.ErrorMessageGeneric;

                setFilenameWithoutExtention(firstFilename);
                setErrorMessage(anyFailed ? aggregateError : '');
                setSharedSuccess(!anyFailed);
            } catch (error) {
                logger(error, "CMFileSharePanel.tsx (Share flow)");
                if (isCurrentOperation(operationId)) {
                    setErrorMessage(languageStrings.ErrorMessageGeneric);
                    setSharedSuccess(false);
                }
            } finally {
                if (isCurrentOperation(operationId)) {
                    setIsCreatingLink(false);
                }
            }
        })();
    }, [isCreatingLink, isMailSendTokenAvailable])

    const getFileParts = (name?: string) => {
        let extention = '';
        let nameWithoutExtention = name || '';

        if (nameWithoutExtention) {
            const lastDotIndex = nameWithoutExtention.lastIndexOf('.');
            if (lastDotIndex > 0 && lastDotIndex < nameWithoutExtention.length - 1) {
                extention = nameWithoutExtention.substring(lastDotIndex + 1);
                nameWithoutExtention = nameWithoutExtention.substring(0, lastDotIndex);
            }
        }

        return {
            extention,
            nameWithoutExtention
        }
    }

    const getItemDisplayName = (item: any): string => {
        return item?.FileLeafRef ?? item?.name ?? '';
    };

    const getItemIconUrl = (item: any): string => {
        try {
            const itemContentTypeId: string = item?.ContentTypeId || item?.currentLocation?.contentTypeId || item?.["contenttypeid"];
            let iconType: IFileTypeIconOptions;

            if (itemContentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
                iconType = { type: FileIconType.docset, size: 16, imageFileType: "svg" };
            }
            else if (itemContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)) {
                iconType = { type: FileIconType.folder, size: 16, imageFileType: "svg" };
            }
            else if (isContainer) {
                iconType = { type: FileIconType.folder, size: 16, imageFileType: "svg" };
            }
            else {
                const fileParts = getFileParts(getItemDisplayName(item));
                iconType = { extension: fileParts.extention?.trim(), size: 16, imageFileType: "svg" };
            }

            return getFileTypeIconAsUrl(iconType);
        } catch {
            return `${process.env.baseUri}/assets/icon-unknown.png`;
        }
    };

    const sendShareMailForItem = async (
        item: any,
        recipients: string[],
        getGraphToken: () => Promise<string>,
        mailAccessToken: string
    ): Promise<ShareMailResult> => {
        const fileParts = getFileParts(item?.FileLeafRef ?? item?.name);

        try {
            const result = await resolveItemShareLink(item, getGraphToken, recipients);

            if (result.error || !result.shareLink) {
                return { ok: false, error: result.error || languageStrings.ErrorMessageGeneric, filename: fileParts.nameWithoutExtention };
            }

            let iconUrl: string;
            try {
                let iconType: IFileTypeIconOptions;
                if (result.itemContentTypeId
                    && (result.itemContentTypeId.startsWith(SharePointFolderContentTypeIds.documentSet)
                        || result.itemContentTypeId.startsWith(SharePointFolderContentTypeIds.folder))
                ) {
                    if (result.itemContentTypeId.startsWith(SharePointFolderContentTypeIds.documentSet)) {
                        iconType = { type: FileIconType.docset, size: 48, imageFileType: "svg" };
                    }
                    else if (result.itemContentTypeId.startsWith(SharePointFolderContentTypeIds.folder)) {
                        iconType = { type: FileIconType.folder, size: 48, imageFileType: "svg" };
                    }
                }

                if (!iconType) {
                    if (isContainer) {
                        iconType = { type: FileIconType.folder, size: 48, imageFileType: "svg" };
                    }
                    else {
                        iconType = { extension: fileParts.extention?.trim(), size: 48, imageFileType: "svg" };
                    }
                }

                iconUrl = getFileTypeIconAsUrl(iconType);
            } catch (error) {
                iconUrl = `${process.env.baseUri}/assets/icon-unknown.png`;
            }

            const sharedByDisplayName = user?.account?.displayName ?? userAccount?.displayName ?? '';
            const sharedByText: string = (result.isFolderOrDocumentSet
                ? languageStrings.FolderSharedWithYouBy
                : languageStrings.DocumentSharedWithYouBy)
                .replace("[person]", sharedByDisplayName);

            const res = await sendMail({
                accessToken: mailAccessToken,
                html: getEmailTemplateHtml(
                    result.shareLink,
                    iconUrl,
                    sharedByText,
                    fileParts.nameWithoutExtention,
                    message,
                    languageStrings.Open
                ),
                subject: languageStrings.DocumentSharedWithYouByEmailSubject.replace("[person]", sharedByDisplayName).replace("[file_name]", fileParts.nameWithoutExtention),
                toRecipients: recipients
            });

            if (res.isError) {
                const mailError = res.error?.["data"]?.["error"]?.["message"] || languageStrings.ErrorMessageGeneric;
                logger(mailError, "CMFileSharePanel.tsx (sendMail)");
                return { ok: false, error: mailError, filename: fileParts.nameWithoutExtention };
            }

            return { ok: true, filename: fileParts.nameWithoutExtention };
        } catch (error) {
            logger(error, "CMFileSharePanel.tsx (sendShareMailForItem)");
            return { ok: false, error: languageStrings.ErrorMessageGeneric, filename: fileParts.nameWithoutExtention };
        }
    };

    const resolveItemShareLink = async (item: any, getToken?: () => Promise<string>, recipients?: string[]): Promise<{
        shareLink?: string;
        itemContentTypeId?: string;
        isFolderOrDocumentSet?: boolean;
        error?: string;
    }> => {
        try {
            let cachedGraphTokenPromise: Promise<string> | undefined;
            const getGraphToken = async (): Promise<string> => {
                if (getToken) return getToken();
                if (!cachedGraphTokenPromise) {
                    cachedGraphTokenPromise = authModule.getGraphAccessToken();
                }
                return cachedGraphTokenPromise;
            };

            let driveId = '';
            let itemId = '';

            if (item.driveId && item.id) {
                driveId = item.driveId;
                itemId = item.id;
            }
            else if (item.driveId && item.itemId) {
                driveId = item.driveId;
                itemId = item.itemId;
            }
            else if (item.driveid && item["serverrelativepath"]) {
                driveId = item.driveid;
                itemId = item.itemid ?? await getFileItemIdFromRelativePath(await getGraphToken(), item.driveid, item["serverrelativepath"]);
            }
            else {
                const spItemUrlparts = item['.spItemUrl'].split('/');
                driveId = isInOneDrive.current ? oneDriveRootDetails?.driveId : (currentDirectory?.parentId ? currentDirectory?.parentId : currentDirectory?.id);
                itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
            }

            if (!itemId) {
                const driveUrl = new URL(await getItemWebViewUrl(await getGraphToken(), driveId, null));
                const folderUrl = new URL(driveUrl.origin + item['FileRef']);
                itemId = await getFileItemIdFromRelativePath(await getGraphToken(), driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
            }

            if (!itemId) {
                return { error: languageStrings.ErrorMessageGeneric };
            }

            let shareLink: string;
            if (canCreateSharingLink) {
                const request: IGraphCreateShareableLinkRequest = {
                    driveId,
                    itemId,
                    message,
                    type: selectedType.key === ShareableLinkRequestType.Edit
                        ? ShareableLinkRequestType.Edit
                        : blockDownload === true
                            ? ShareableLinkRequestType.BlocksDownload
                            : ShareableLinkRequestType.View,
                    scope: selectedScope
                };

                if (selectedScope === ShareableLinkRequestScope.Users) {
                    request.recipients = recipients ?? normalizeRecipients(selectedPeople);
                }

                const createLinkResults = await createItemShareableLink(request, await getGraphToken());
                if (createLinkResults?.link?.webUrl) {
                    shareLink = createLinkResults.link.webUrl;
                }
            }
            else {
                shareLink = await getItemWebViewUrl(await getGraphToken(), driveId, itemId);
            }

            if (!shareLink) {
                logger(new Error(`Sharing link could not be created for request ${JSON.stringify({ type: selectedType.key === ShareableLinkRequestType.Edit ? ShareableLinkRequestType.Edit : blockDownload === true ? ShareableLinkRequestType.BlocksDownload : ShareableLinkRequestType.View, scope: selectedScope })}`), "CMFileSharePanel.tsx (resolveItemShareLink)");
                return { error: languageStrings.ErrorMessageGeneric };
            }

            const itemContentTypeId: string = item.ContentTypeId || item.currentLocation?.contentTypeId || item["contenttypeid"];
            let isFolderOrDocumentSet: boolean = isContainer === true;
            if (!isFolderOrDocumentSet && itemContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)) {
                isFolderOrDocumentSet = true;
            }

            if (isFolderOrDocumentSet) {
                shareLink = `${process.env.contentManagerUrl}?${contentManager.params.preloadURL}=${tryEncodeUriComponent(shareLink)}`;
            }

            return { shareLink, itemContentTypeId, isFolderOrDocumentSet };
        } catch (error) {
            logger(error, "CMFileSharePanel.tsx (resolveItemShareLink)");
            return { error: languageStrings.ErrorMessageGeneric };
        }
    };

    const generateCopyLinksBatch = async (actionableItems: any[], recipients: string[]): Promise<CopyLinksBatch> => {
        let sharedGraphTokenPromise: Promise<string> | undefined;
        const getSharedToken = async (): Promise<string> => {
            if (!sharedGraphTokenPromise) {
                sharedGraphTokenPromise = authModule.getGraphAccessToken();
            }
            return sharedGraphTokenPromise;
        };

        const itemResults = await mapItemsWithLimit(actionableItems, async item => {
            const result = await resolveItemShareLink(item, getSharedToken, recipients);
            if (result.shareLink) {
                return { item, link: result.shareLink };
            }
            return { item, error: result.error || languageStrings.ErrorMessageGeneric };
        });

        return {
            itemResults,
            hasFailedLinks: itemResults.some(r => !!r.error),
            errorMessage: itemResults.find(r => r.error)?.error ?? "",
        };
    };

    const getSuccessfulLinks = (itemResults: CopyLinkItemResult[]): { item: any; link: string }[] =>
        itemResults.filter(r => !!r.link).map(r => ({ item: r.item, link: r.link! }));

    const handleClipboardFallback = async (batch: CopyLinksBatch | null, operationId: number) => {
        const successful = batch ? getSuccessfulLinks(batch.itemResults) : [];
        if (!batch || successful.length === 0) {
            if (isCurrentOperation(operationId)) {
                setErrorMessage(batch?.errorMessage || languageStrings.ErrorMessageGeneric);
                setSharedSuccess(false);
                setCopyStatus(initialCopyStatus);
            }
            return;
        }

        const text = successful.map(s => s.link).join("\r\n");

        try {
            await navigatorClipboardWriteText(text);
            if (!isCurrentOperation(operationId)) return;

            setCopyStatus({
                ...initialCopyStatus,
                copiedLinkCount: successful.length,
            });
            setSharedSuccess(!batch.hasFailedLinks);
            if (batch.hasFailedLinks) {
                setErrorMessage(batch.errorMessage || languageStrings.ErrorMessageGeneric);
            }
        } catch (error) {
            logger(error, "CMFileSharePanel.tsx (Clipboard execCommand fallback)");
            if (isCurrentOperation(operationId)) {
                setSharedSuccess(false);
                setCopyStatus({
                    ...initialCopyStatus,
                    manualItems: successful,
                    manualCopyHasFailedLinks: batch.hasFailedLinks,
                });
                if (batch.hasFailedLinks) {
                    setErrorMessage(batch.errorMessage || languageStrings.ErrorMessageGeneric);
                }
            }
        }
    };

    // MUST be invoked synchronously inside a user-gesture handler. Do NOT `await`
    // anything before calling this - Safari/WebKit require navigator.clipboard.write
    // to be reached without any awaits after the gesture, or it throws NotAllowedError.
    const runCopyMode = (actionableItems: any[], recipients: string[], operationId: number) => {
        setIsCreatingLink(true);

        const linksResultPromise = generateCopyLinksBatch(actionableItems, recipients);

        const canUsePromiseClipboard =
            typeof ClipboardItem !== 'undefined'
            && !!navigator.clipboard?.write
            && typeof window !== 'undefined'
            && window.isSecureContext;

        let clipboardWritePromise: Promise<void> | null = null;
        if (canUsePromiseClipboard) {
            try {
                const blobPromise = linksResultPromise.then(batch => {
                    const successful = getSuccessfulLinks(batch.itemResults);
                    if (successful.length === 0) {
                        throw new Error(batch.errorMessage || languageStrings.ErrorMessageGeneric);
                    }
                    return new Blob([successful.map(s => s.link).join("\r\n")], { type: 'text/plain' });
                });

                // Constructed and invoked synchronously so the click's user-gesture activation is preserved.
                // Safari / WebKit require clipboard.write to be reached without any awaits after the gesture.
                clipboardWritePromise = navigator.clipboard.write([new ClipboardItem({ 'text/plain': blobPromise })]);
            } catch (error) {
                // ClipboardItem constructor or clipboard.write can throw synchronously on browsers with partial
                // support (older Firefox, some WebViews). Fall through to execCommand in the same tick so we
                // do not lose user-gesture activation.
                logger(error, "CMFileSharePanel.tsx (ClipboardItem construct sync throw)");
                clipboardWritePromise = null;
            }
        }

        if (clipboardWritePromise) {
            (async () => {
                try {
                    await clipboardWritePromise;
                    const batch = await linksResultPromise;
                    if (!isCurrentOperation(operationId)) return;

                    const successful = getSuccessfulLinks(batch.itemResults);
                    if (successful.length === 0) {
                        setErrorMessage(batch.errorMessage || languageStrings.ErrorMessageGeneric);
                        setSharedSuccess(false);
                        setCopyStatus(initialCopyStatus);
                        return;
                    }

                    setCopyStatus({
                        ...initialCopyStatus,
                        copiedLinkCount: successful.length,
                    });
                    setSharedSuccess(!batch.hasFailedLinks);
                    if (batch.hasFailedLinks) {
                        setErrorMessage(batch.errorMessage || languageStrings.ErrorMessageGeneric);
                    }
                } catch (error) {
                    logger(error, "CMFileSharePanel.tsx (Clipboard write ClipboardItem)");
                    const batch = await linksResultPromise.catch(() => null);
                    await handleClipboardFallback(batch, operationId);
                } finally {
                    if (isCurrentOperation(operationId)) {
                        setIsCreatingLink(false);
                    }
                }
            })();
        }
        else {
            (async () => {
                try {
                    const batch = await linksResultPromise.catch(() => null);
                    await handleClipboardFallback(batch, operationId);
                } finally {
                    if (isCurrentOperation(operationId)) {
                        setIsCreatingLink(false);
                    }
                }
            })();
        }
    };

    const copyAllManualLinks = async () => {
        const manualItems = copyStatus.manualItems;
        if (!manualItems || manualItems.length === 0) return;
        const operationId = operationIdRef.current;
        const text = manualItems.map(e => e.link).join("\r\n");
        try {
            if (!generalModule.getIsOfficeJSAvailable() && navigator.clipboard?.writeText && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else {
                await navigatorClipboardWriteText(text);
            }
            if (!isCurrentOperation(operationId)) return;

            if (!copyStatus.manualCopyHasFailedLinks) {
                setCopyStatus({
                    ...initialCopyStatus,
                    copiedLinkCount: manualItems.length,
                });
                setSharedSuccess(true);
                setErrorMessage("");
                return;
            }
            setCopyStatus(prev => ({
                ...prev,
                copiedLinkCount: manualItems.length,
                manualCopyCompleted: true,
                manualCopyButtonFailed: false,
            }));
            clearCopiedResetTimer();
            copiedResetTimerRef.current = window.setTimeout(() => {
                if (isCurrentOperation(operationId)) {
                    setCopyStatus(prev => ({
                        ...prev,
                        manualCopyCompleted: false,
                    }));
                    copiedResetTimerRef.current = null;
                }
            }, 2000);
        } catch (error) {
            logger(error, "CMFileSharePanel.tsx (copyAllManualLinks)");
            if (isCurrentOperation(operationId)) {
                setCopyStatus(prev => ({
                    ...prev,
                    manualCopyCompleted: false,
                    manualCopyButtonFailed: true,
                }));
            }
        }
    };

    const createShareableLink = async () => {
        operationIdRef.current += 1;
        const operationId = operationIdRef.current;
        const actionableItems = getActionableItems();
        const recipients = normalizeRecipients(selectedPeople);

        setSharedSuccess(false);
        setCopyStatus(initialCopyStatus);
        clearCopiedResetTimer();
        setErrorMessage("");

        if (actionableItems.length === 0) {
            setErrorMessage(languageStrings.ErrorMessageGeneric);
            return;
        }

        if (selectedScope === ShareableLinkRequestScope.Users && recipients.length === 0) {
            setErrorMessage(languageStrings.PleaseSelectSomeoneShare);
            return;
        }

        if (mode === SharePanelMode.Copy) {
            runCopyMode(actionableItems, recipients, operationId);
            return;
        }

        setIsCreatingLink(true);
        if (!isMailSendTokenAvailable) {
            try {
                const tokenAvailable = await requestMailSendToken();
                if (!isCurrentOperation(operationId)) return;

                if (!tokenAvailable) {
                    setIsCreatingLink(false);
                    setSharedSuccess(false);
                    setErrorMessage(languageStrings.ErrorMessageGeneric);
                }
            } catch (error) {
                logger(error, "CMFileSharePanel.tsx (requestMailSendToken)");
                if (isCurrentOperation(operationId)) {
                    setIsCreatingLink(false);
                    setSharedSuccess(false);
                    setErrorMessage(languageStrings.ErrorMessageGeneric);
                }
            }
        }
    }

    const dismissPanel = () => {
        operationIdRef.current += 1;
        clearCopiedResetTimer();
        closePanel();
    };

    const onRenderFooterContent = () => (
        <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }}>
            {!sharedSuccess && <PrimaryButton
                iconProps={mode === SharePanelMode.Copy
                    ? { iconName: "Link" }
                    : { iconName: "Send" }}
                onClick={() => {
                    createShareableLink();
                }}
                disabled={isCreatingLink || isMailSendTokenRequestInProgress}
                text={mode === SharePanelMode.Copy
                    ? languageStrings.CopyLink
                    : languageStrings.Send}
            >
                {(isCreatingLink) && <Spinner size={SpinnerSize.small} styles={{ root: { paddingLeft: 5 } }} />}
            </PrimaryButton>}
            {sharedSuccess && <DefaultButton
                iconProps={{ iconName: "Checkmark" }}
                onClick={() => {
                    dismissPanel();
                }}
                text={languageStrings.Close}
            />}
        </Stack>
    );

    const onTypeChange = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        setSelectedType(item);
    };

    const onScopeChange = (ev: React.FormEvent<HTMLInputElement>, option: IChoiceGroupOption): void => {
        setSelectedScope(option.key as ShareableLinkRequestScope);
    }

    return (
        <Panel
            isOpen={panelIsOpen}
            headerText={languageStrings.Share}
            onDismiss={dismissPanel}
            isLightDismiss
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={!generalModule.isInConentMangerMode() ? "300px" : "450px"}
            styles={panelStyles}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            <div className={scrollStyle} >
                <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                    <Stack tokens={stackTokens} style={{ marginTop: '15px' }}>
                        {errorMessage && <MessageBar messageBarType={MessageBarType.error} isMultiline={true} dismissButtonAriaLabel="Close" onDismiss={() => setErrorMessage("")}>
                            {errorMessage}
                        </MessageBar>}
                        {sharedSuccess && <MessageBar messageBarType={MessageBarType.success} isMultiline={false} dismissButtonAriaLabel="Close">
                            {mode === SharePanelMode.Share
                                ? languageStrings.DocumentShareSuccess.replace("[file_name]", filenameWithoutExtention)
                                : `${copyStatus.copiedLinkCount} ${languageStrings.SharedLinkSuccessfullyCopied}`}
                        </MessageBar>}
                        {copyStatus.manualItems && copyStatus.manualItems.length > 0 && (
                            <MessageBar
                                messageBarType={MessageBarType.warning}
                                isMultiline={true}
                                actions={
                                    <div>
                                        <MessageBarButton
                                            iconProps={{ iconName: copyStatus.manualCopyCompleted ? "Checkmark" : "Copy" }}
                                            onClick={copyAllManualLinks}
                                            ariaLabel={copyStatus.manualCopyCompleted ? languageStrings.Copied : languageStrings.CopyLink}
                                        >
                                            {copyStatus.manualCopyCompleted ? languageStrings.Copied : languageStrings.CopyLink}
                                        </MessageBarButton>
                                    </div>
                                }
                            >
                                <Stack tokens={{ childrenGap: 8 }}>
                                    <span>
                                        {copyStatus.manualCopyButtonFailed
                                            ? languageStrings.ClipboardBlockedInstructions
                                            : languageStrings.ManualClipboardCopyInstructions}
                                    </span>
                                    <Stack tokens={{ childrenGap: 4 }}>
                                        {copyStatus.manualItems.map((manualItem, idx) => {
                                            const filename = getItemDisplayName(manualItem.item);
                                            const iconUrl = getItemIconUrl(manualItem.item);
                                            return (
                                                <Stack
                                                    key={idx}
                                                    horizontal
                                                    verticalAlign="center"
                                                    tokens={{ childrenGap: 8 }}
                                                    styles={{ root: { minWidth: 0 } }}
                                                >
                                                    <img src={iconUrl} alt="" width={16} height={16} style={{ flexShrink: 0 }} />
                                                    <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
                                                        <TooltipHost content={filename} directionalHint={DirectionalHint.topLeftEdge}>
                                                            <div className={truncatedFilenameStyle}>{filename}</div>
                                                        </TooltipHost>
                                                    </Stack.Item>
                                                </Stack>
                                            );
                                        })}
                                    </Stack>
                                    {copyStatus.manualCopyButtonFailed && (
                                        <TextField
                                            ariaLabel={languageStrings.CopyLink}
                                            value={copyStatus.manualItems.map(e => e.link).join("\r\n")}
                                            multiline
                                            readOnly
                                            rows={Math.min(Math.max(copyStatus.manualItems.length, 2), 6)}
                                        />
                                    )}
                                </Stack>
                            </MessageBar>
                        )}
                        <div className={sectionStyle}>
                            {items?.map((item, index) => (
                                <div key={index}>
                                    <Fileview
                                        item={item}
                                        isMobile={isMobile}
                                        multiSelect={false}
                                        hasShowMore={false}
                                        tenantEnablePII={false}
                                        nonClickable={true}
                                    />
                                </div>
                            ))}
                        </div>
                        {showMore}
                        {mode === SharePanelMode.Copy && <ChoiceGroup label={languageStrings.ShareLinkWith} defaultSelectedKey={selectedScope} options={scopes} onChange={onScopeChange} />}
                        {selectedScope === ShareableLinkRequestScope.Users && <PeopleFinder label={languageStrings.ShareWithPeople} selectedPersonasChanged={selectedPersonasChanged} />}
                        {mode === SharePanelMode.Share && <TextField label={languageStrings.Message} value={message} multiline rows={5} onChange={(event, newValue) => setMessage(newValue)} />}
                        {canCreateSharingLink && <Dropdown
                            label={languageStrings.Type}
                            options={types}
                            selectedKey={selectedType.key}
                            onChange={onTypeChange}
                        />}
                        {canCreateSharingLink && selectedType.key === ShareableLinkRequestType.View && <Toggle label={languageStrings.BlockDownload} checked={blockDownload} onText={languageStrings.Yes} offText={languageStrings.No} onChange={() => {
                            setBlockDownload(prev => !prev);
                        }} />}
                        {activityItem && <StackItem>
                            <Label>{languageStrings.SharedWith}</Label>
                            <TooltipHost content={tooltipContent} directionalHint={DirectionalHint.topLeftEdge}>
                                <ActivityItem {...(activityItem as IActivityItemProps)} />
                            </TooltipHost>
                        </StackItem>}
                    </Stack>
                </ScrollablePane>
            </div>
        </Panel>
    )
};
